import { useEffect, useRef, useState, createContext, useContext, useCallback } from 'react';
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import Lenis from 'lenis';
import axios from 'axios';
import { Toaster } from './components/ui/sonner';
import ChatWidget from './components/ChatWidget';
import Landing from './pages/Landing';
import Account from './pages/Account';
import Admin from './pages/Admin';
import AdminProjects from './pages/admin/AdminProjects';
import AdminCrm from './pages/admin/AdminCrm';
import AdminCalendar from './pages/admin/AdminCalendar';
import AdminSettings from './pages/admin/AdminSettings';
import Login from './pages/Login';
import ResetPassword from './pages/ResetPassword';
import VerifyEmail from './pages/VerifyEmail';
import Offer from './pages/Offer';
import Book from './pages/Book';
import Review from './pages/Review';
import PaymentSuccess from './pages/PaymentSuccess';
import PaymentCancel from './pages/PaymentCancel';
import './App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API, withCredentials: true });

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const res = await api.get('/auth/me');
      setUser(res.data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const ref = new URLSearchParams(window.location.search).get('ref');
    if (ref) sessionStorage.setItem('gslab_ref', ref);
  }, []);

  useEffect(() => {
    // CRITICAL: returning from OAuth callback — AuthCallback exchanges session_id first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/account';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const loginEmail = async (email, password) => {
    const res = await api.post('/auth/login', { email, password });
    setUser(res.data);
    return res.data;
  };

  const registerEmail = async (name, email, password, phone = '') => {
    const res = await api.post('/auth/register', { name, email, password, phone, origin_url: window.location.origin });
    // Unverified accounts are inactive until the email link is clicked — no session yet.
    if (!res.data?.needs_verification) setUser(res.data);
    return res.data;
  };

  const logout = async () => {
    try { await api.post('/auth/logout'); } catch { /* noop */ }
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, loginEmail, registerEmail, logout, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

function AuthCallback() {
  const location = useLocation();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    const sessionId = new URLSearchParams(location.hash.replace('#', '')).get('session_id');
    (async () => {
      try {
        const res = await api.post('/auth/session', { session_id: sessionId });
        setUser(res.data);
        navigate('/account', { replace: true, state: { user: res.data } });
      } catch {
        navigate('/', { replace: true });
      }
    })();
  }, [location.hash, navigate, setUser]);

  return (
    <div className="min-h-screen bg-[#050B14] flex items-center justify-center">
      <div className="font-mono-tech text-amber-400/80 text-sm tracking-widest animate-pulse">ESTABLISHING SECURE SESSION…</div>
    </div>
  );
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/account" element={<Account />} />
      <Route path="/admin" element={<Admin />} />
      <Route path="/admin/projects" element={<AdminProjects />} />
      <Route path="/admin/crm" element={<AdminCrm />} />
      <Route path="/admin/calendar" element={<AdminCalendar />} />
      <Route path="/admin/settings" element={<AdminSettings />} />
      <Route path="/login" element={<Login />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/offer/:pkgId" element={<Offer />} />
      <Route path="/book" element={<Book />} />
      <Route path="/review" element={<Review />} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/cancel" element={<PaymentCancel />} />
    </Routes>
  );
}

function App() {
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    const onAnchor = (e) => {
      const a = e.target.closest('a[href^="#"]');
      if (!a) return;
      const el = document.querySelector(a.getAttribute('href'));
      if (el) {
        e.preventDefault();
        lenis.scrollTo(el, { offset: -90 });
      }
    };
    document.addEventListener('click', onAnchor);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
      document.removeEventListener('click', onAnchor);
    };
  }, []);

  return (
    <BrowserRouter>
      <AuthProvider>
        <div className="grain">
          <AppRouter />
          <ChatWidget />
          <Toaster theme="dark" position="bottom-right" />
        </div>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
